import { api } from "./api";

export type RetreatStatus = "draft" | "open" | "sold-out" | "past";

export type RetreatSummary = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  tagline: string | null;
  location: string;
  venue: string | null;
  startDate: string;
  endDate: string;
  capacity: number | null;
  status: RetreatStatus;
  heroMediaId: string | null;
  heroImageUrl: string | null;
};

export type PriceTier = {
  name: string;
  tierType: "deposit" | "full" | "addon";
  priceCents: number;
  balanceDueCents?: number;
  description?: string;
};

export type Included = { icon?: string; label: string };
export type Detail = { icon?: string; label: string };
export type Project = {
  name: string;
  description?: string;
  kitContents?: { item: string }[];
};
export type ScheduleDay = {
  day: string;
  label?: string;
  activities?: { time?: string; activity: string }[];
};
export type Faq = { question: string; answer: string };
export type Sponsor = {
  name: string;
  logoMediaId?: string;
  logoUrl?: string;
  websiteUrl?: string;
};

export type RetreatDetail = RetreatSummary & {
  instagramEmbedUrl: string | null;
  descriptionMd: string | null;
  aboutBodyMd: string | null;
  included: Included[];
  details: Detail[];
  projects: Project[];
  schedule: ScheduleDay[];
  travelInfo: string | null;
  sponsors: Sponsor[];
  priceTiers: PriceTier[];
  externalRegistrationUrl: string | null;
  faq: Faq[];
};

export type RetreatWaitlist = {
  id: string;
  name: string;
  slug: string;
  status: "active" | "disabled";
};

export type RetreatsResponse = { retreats: RetreatSummary[] };
export type RetreatDetailResponse = {
  retreat: RetreatDetail;
  waitlist: RetreatWaitlist | null;
};

let retreatsPromise: Promise<RetreatsResponse> | null = null;
const retreatDetailPromises = new Map<string, Promise<RetreatDetailResponse>>();

export function loadRetreats() {
  if (!retreatsPromise) {
    retreatsPromise = api
      .get<RetreatsResponse>("/api/retreats")
      .then((response) => {
        prefetchRetreatDetails(response.retreats);
        return response;
      })
      .catch((error) => {
        retreatsPromise = null;
        throw error;
      });
  }
  return retreatsPromise;
}

export function loadRetreatDetail(slug: string) {
  const key = slug.trim();
  let promise = retreatDetailPromises.get(key);
  if (!promise) {
    promise = api
      .get<RetreatDetailResponse>(`/api/retreats/${encodeURIComponent(key)}`)
      .catch((error) => {
        retreatDetailPromises.delete(key);
        throw error;
      });
    retreatDetailPromises.set(key, promise);
  }
  return promise;
}

export function prefetchRetreatDetail(slug: string) {
  void loadRetreatDetail(slug).catch(() => undefined);
}

export function prefetchRetreatDetails(retreats: Array<Pick<RetreatSummary, "slug">>) {
  for (const retreat of retreats) {
    prefetchRetreatDetail(retreat.slug);
  }
}
